import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const links = [
  { to: "/", label: "Home" },
  { to: "/our-brands", label: "Our Brands" },
  { to: "/the-apartment", label: "The Apartment" },
  { to: "/things-to-do-nearby", label: "Things to Do Nearby" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500`}
        style={{
          background: scrolled ? "rgba(250, 247, 242, 0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(14px)" : "none",
          borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
        }}
      >
        <div className="container-cu flex items-center justify-between py-5">
          <Link
            data-testid="logo-link"
            to="/"
            className="flex items-center gap-3 group"
          >
            <img
              src="/assets/logo_casahaya.png"
              alt="Casa Haya International"
              className="w-11 h-11 rounded-md object-cover shadow-sm"
              style={{ border: scrolled ? "1px solid var(--line)" : "1px solid rgba(250,247,242,0.2)" }}
            />
            <span
              className="font-serif text-lg leading-tight hidden sm:flex flex-col"
              style={{ color: scrolled ? "var(--forest)" : "var(--cream)" }}
            >
              <span className="text-[15px] tracking-wide">Casa Haya</span>
              <span className="text-[10px] uppercase tracking-[0.25em] font-sans opacity-80">
                International BV
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.18em] font-medium transition-colors duration-300 link-underline ${
                    isActive ? "font-semibold" : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: scrolled
                    ? isActive
                      ? "var(--forest)"
                      : "var(--brown-soft)"
                    : isActive
                    ? "var(--gold)"
                    : "rgba(250,247,242,0.92)",
                })}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full"
            style={{
              border: `1px solid ${scrolled ? "var(--line)" : "rgba(250,247,242,0.5)"}`,
              color: scrolled ? "var(--forest)" : "var(--cream)",
            }}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`}></i>
          </button>
        </div>
      </header>

      <div
        data-testid="mobile-menu"
        className={`mobile-menu lg:hidden ${open ? "open" : ""}`}
      >
        <button
          className="absolute top-6 right-6 w-11 h-11 rounded-full inline-flex items-center justify-center"
          style={{ border: "1px solid var(--line)", color: "var(--forest)" }}
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
        <nav className="flex flex-col gap-1 mt-4">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `py-4 border-b font-serif text-2xl ${isActive ? "italic" : ""}`
              }
              style={({ isActive }) => ({
                borderColor: "var(--line)",
                color: isActive ? "var(--gold-deep)" : "var(--forest)",
                animationDelay: `${i * 60}ms`,
              })}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-8 text-sm" style={{ color: "var(--brown-soft)" }}>
          info@casahaya.com
        </div>
      </div>
    </>
  );
}
